/**
 * Centralized Modal/Dialog Types
 *
 * Shared type definitions for modal and dialog data structures used across
 * frontend components.
 */

import type { Id } from '../../convex/_generated/dataModel';
import type { ServiceType, CompensationType, VerificationStatus } from './validators';

// ============================================================================
// Address Selection Types
// ============================================================================

/**
 * Address selection from autocomplete
 */
export interface AddressSelection {
  formattedAddress: string;
  city: string;
  state?: string | undefined;
  lat: number;
  lng: number;
}

// ============================================================================
// New Service Modal Types
// ============================================================================

/**
 * New service form state
 */
export interface NewServiceForm {
  serviceType: ServiceType | '';
  businessLocationId?: string;
  doctorName?: string;
  serviceAreaId?: string;
  manualServiceArea?: string; // DA-474: Manual entry when service area not found
  manualBereitschaftspraxis?: string; // DA-476: Manual entry when BP not found
  manualFahrdienstgebiet?: string; // DA-476: Manual entry when FDG not found
  bereitschaftspraxisId?: string;
  fahrdienstgebietId?: string;
  start: string;
  end: string;
  addressText: string;
  address?: AddressSelection;
  notes: string;
  privateNotes: string;
  hourly: string;
  minPrice?: string;
  maxPrice?: string;
  priceMode?: CompensationType;
  hasNotfallscheinbeteiligung?: boolean;
  notfallscheinbeteiligungRate?: number;
}

/**
 * Initial data for prefilling new service form
 */
export interface NewServiceInitialData {
  draftId?: Id<'providerServiceDrafts'>;
  sourceKey?: string;
  serviceType?: ServiceType | '';
  startDate?: string;
  endDate?: string;
  serviceAreaId?: string;
  // DA-476: Manual entry fields from marketing wizard
  manualServiceArea?: string;
  manualBereitschaftspraxis?: string;
  manualFahrdienstgebiet?: string;
  compensationType?: CompensationType;
  fixedPrice?: number;
  minPrice?: number;
  maxPrice?: number;
  hasNotfallscheinbeteiligung?: boolean;
  notfallscheinbeteiligungRate?: number;
  notes?: string;
}

// ============================================================================
// Assign Substitute Dialog Types
// ============================================================================

/**
 * Application data for assign substitute dialog
 */
export interface AssignSubstituteApplication {
  _id: Id<'applications'>;
  applicantId: Id<'users'>;
  applicantName: string;
  applicantEmail: string;
  status: string;
  proposedCompensation?: number;
  message?: string;
  appliedAt: number;
  applicantVerificationStatus?: VerificationStatus;
  applicantInsuranceExpiresAt?: number;
}

/**
 * Assignment mode for substitute assignment
 */
export type AssignmentMode = 'application' | 'email';

// ============================================================================
// Onboarding Dialog Types
// ============================================================================

/**
 * Wizard step for onboarding flow
 */
export type WizardStep = 'intro' | 'personal' | 'qualifications' | 'address' | 'recommendations' | 'documents' | 'location';

/**
 * Wizard steps by role
 */
export const SUBSTITUTE_STEPS: WizardStep[] = ['intro', 'personal', 'qualifications', 'address', 'recommendations', 'documents'];
export const PROVIDER_STEPS: WizardStep[] = ['intro', 'personal', 'location'];

// ============================================================================
// Contract Modal Types
// ============================================================================

/**
 * Contract data for upload modal
 * Used by: upload-custom-contract-modal.tsx
 */
export interface ContractData {
  dutyTitle: string;
  dutyStartDate: number;
  dutyEndDate: number;
  dutyLocation: string;
  providerName: string;
  representativeName: string;
  hourlyRate: number;
  hoursEstimated: number;
  baseCompensationEstimated: number;
  hasNotfallscheinbeteiligung: boolean;
  notfallscheinbeteiligungRate?: number;
  notfallscheinbeteiligungTerms?: string;
}

/**
 * Props for UploadCustomContractModal
 * Used by: upload-custom-contract-modal.tsx
 */
export interface UploadCustomContractModalProps {
  contractId: string;
  contractData: ContractData;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}
